import { toaster } from "@/web/components/primitives/toast";
import { ErrorHandler } from "@/web/lib/errors";

import type { Uuid } from "spacetimedb";

// ---------------------------------------------------------------------------
// Sharing a business card's recipient link.
//
// These live in `lib/` rather than beside the SMB dashboard's share action
// because the PUBLIC TTW page shares the same link from its hero button.
// Importing them from `-actions/share/ShareBusinessCardAction` would pull the
// whole dashboard tree (action cards, drawers, the rules engine) into the
// public route's bundle for two small functions.
// ---------------------------------------------------------------------------

function canUseWebShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** Whether to label the control "Share link" or "Copy link". */
export function shareControlLabel(): "Share link" | "Copy link" {
  return canUseWebShare() ? "Share link" : "Copy link";
}

/**
 * Recipient-facing URL for a `shareableBusinessCards_v3` row. The slug is the
 * row's UUIDv7 in canonical string form. If we ever add a vanity slug column,
 * this composer is the only place that needs to swap.
 */
export function shareableCardUrl(cardId: Uuid): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/ttw/${cardId.toString()}`;
}

/**
 * Web Share API when available; otherwise copy to clipboard.
 *
 * A user dismissing the native share sheet throws `AbortError` — that's a
 * deliberate cancel, not a failure, so it returns silently rather than falling
 * back to a clipboard write the user didn't ask for.
 */
export async function shareBusinessCardLink(shareUrl: string): Promise<void> {
  try {
    if (canUseWebShare()) {
      await navigator.share({ url: shareUrl });
    } else if (shareUrl !== "" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      toaster({ message: "Link copied to clipboard.", variant: "success" });
    }
  } catch (e) {
    if ((e as { name?: string }).name === "AbortError") {
      return;
    }
    ErrorHandler.logError(e);
    if (shareUrl !== "" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toaster({ message: "Link copied to clipboard.", variant: "success" });
      } catch (clipErr: unknown) {
        ErrorHandler.logError(clipErr);
      }
    }
  }
}
